import * as React from "react"

import { ProductFields, RTF } from "@tenforce/product-fields"

import { GlobalState } from "../../../../store"
import { useAppDispatch, useAppSelector } from "../../../../hooks"

import { IRichTextField } from "../.."
import { setCurrentFieldInEditMode, updateField } from "../../slice"
import { addAttachment } from "./slice"
import HTMLAndInternalLinkParser from "../common/HTMLAndInternalLinkParser"
import { Labels } from "../../../../util/Translations"

export interface OwnProps {
    field: IRichTextField
}

type Props = OwnProps

export const RichTextField = ({ field }: Props): JSX.Element => {
    const { ClickHereToEdit } = Labels
    const dispatch = useAppDispatch()
    const [headerSize, setHeaderSize] = React.useState({ width: 0, height: 0 })

    React.useEffect(() => {
        const element = document.querySelector("header.NewUI-item-header")

        if (!element) {
            return
        }

        const { offsetWidth: width, offsetHeight: height } =
            element as HTMLElement
        setHeaderSize({ width, height })

        const handleResize = (entries: ResizeObserverEntry[]): void => {
            for (const entry of entries) {
                if (entry.target === element) {
                    setHeaderSize({
                        width: entry.contentRect.width,
                        height: entry.contentRect.height,
                    })
                }
            }
        }

        const resizeObserver = new ResizeObserver(handleResize)
        resizeObserver.observe(element)

        return () => {
            resizeObserver.unobserve(element)
            resizeObserver.disconnect()
        }
    }, [])

    const { isLoading: isFieldLoading, currentFieldInEditMode = "" } =
        useAppSelector((state: GlobalState) => state.fields)
    const isRichTextLoading = useAppSelector(
        (state: GlobalState) => state.richText.isLoading,
    )
    const { loadingResources } = useAppSelector(
        (state: GlobalState) => state.resources,
    )

    const {
        odataType: _odataType,
        value = "",
        description,
        itemId,
        maxLength,
        position: _position,
        ...fieldProps
    } = field

    const handleOnSave = React.useCallback(
        async (newValue: string): Promise<void> => {
            if (newValue === value) {
                return
            }
            await dispatch(
                updateField({
                    field,
                    newValue,
                }),
            ).unwrap()
        },
        [value, dispatch, field, updateField],
    )

    const onFilesAdded = React.useCallback(
        async (files: File[]): Promise<ProductFields.Attachment[]> => {
            // Await for the result here because it is immediately returned to the child component.
            const results = await dispatch(addAttachment({ files, field }))
            return (results.payload as ProductFields.Attachment[]) || []
        },
        [addAttachment, dispatch, field],
    )

    const isLoading =
        fieldProps.isLoading ||
        isFieldLoading ||
        isRichTextLoading ||
        loadingResources.length > 0

    const isDisabled =
        fieldProps.isDisabled ||
        !["", fieldProps.fieldId].includes(currentFieldInEditMode)

    return (
        <RTF
            {...fieldProps}
            isLoading={isLoading}
            onSave={handleOnSave}
            extraClasses="tw-w-full"
            placeholder={fieldProps.isReadOnly ? "" : ClickHereToEdit}
            value={value}
            onFilesAdded={onFilesAdded}
            isFileUploadEnabled={false}
            isImageUploadEnabled={true}
            description={
                <HTMLAndInternalLinkParser
                    content={(field?.description as string) ?? ""}
                />
            }
            stickyToolbarOffset={headerSize.height}
            maxLength={maxLength}
            formatValue={(value: string): JSX.Element => {
                return (
                    <HTMLAndInternalLinkParser
                        content={value}
                        parseHTML={true}
                    />
                )
            }}
            isDisabled={isDisabled}
            onFieldEnterEditMode={(fieldId): void => {
                dispatch(
                    setCurrentFieldInEditMode(fieldId ?? fieldProps.fieldId),
                )
            }}
            onFieldExitEditMode={(): void => {
                dispatch(setCurrentFieldInEditMode(""))
            }}
        />
    )
}

export default RichTextField
